import { ShieldCheck, Globe, Accessibility, Users, HeartHandshake, Languages } from "lucide-react";
import { useDeployment } from "@/lib/DeploymentContext";

const getCommitments = (d) => [
  {
    icon: Users,
    title: "All ethnic and social backgrounds",
    body: d.demographicLineFull,
  },
  {
    icon: ShieldCheck,
    title: "No prejudice, no preconditions, no litmus tests",
    body:
      "Service is not contingent on documentation status, voucher status, religion, gender identity, sexual orientation, criminal record, family composition, or political belief. The only criterion is need.",
  },
  {
    icon: Languages,
    title: "Bilingual by default · multilingual by design",
    body:
      "Site, intake materials, and case-management protocols are delivered in English and Spanish from day one. Additional language access (Vietnamese, Arabic, Haitian Creole, others) added on demand at any deployment.",
  },
  {
    icon: Accessibility,
    title: "Accessibility & accommodation",
    body:
      "ADA-compliant intake locations, mobility-accommodating home visits, large-print materials for senior clients, and disability-rights coordination with Harris Health and Texas DARS built into every Service Connector's caseload.",
  },
  {
    icon: HeartHandshake,
    title: "Faith-rooted, never faith-required",
    body:
      "The Bridge Model is built on a tradition of faith-rooted community service — but participation never requires religious affiliation or practice. We serve every household with the same care, period.",
  },
  {
    icon: Globe,
    title: "Public accountability for equity outcomes",
    body:
      "The independent annual evaluation publishes service distribution by ethnicity, language, age, and household type at every deployment. If any group is underserved, the agency in question loses its contract.",
  },
];

export default function EquityCommitment() {
  const d = useDeployment();
  const commitments = getCommitments(d);
  return (
    <section
      id="equity"
      data-testid="section-equity"
      className="bg-[#F9F7F1] py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                Equity &amp; Access Commitment
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Every underserved household.<br />
              <em className="italic">Without prejudice. Without precondition.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              The Bridge Model serves underserved households of <strong>every
              ethnic and social background</strong>. {d.demographicLine} and the rest of the community —
              and Bridge mirrors that community without filtering, gatekeeping, or selecting which
              families &ldquo;deserve&rdquo; to be helped. The Service Connector is bound to
              the household by need, not by category.
            </p>
          </div>
        </div>

        {/* Six commitment cards */}
        <ul
          data-testid="equity-grid"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12"
        >
          {commitments.map((c, i) => {
            const Icon = c.icon;
            return (
              <li
                key={c.title}
                data-testid={`equity-pillar-${i}`}
                className="bg-white p-6 lg:p-7 flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className="h-6 w-6 text-[#8B0000]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-base lg:text-lg text-[#0A192F] leading-snug mb-2">
                  {c.title}
                </h3>
                <p
                  className="text-xs lg:text-sm text-[#475569] leading-relaxed flex-1"
                  dangerouslySetInnerHTML={{ __html: c.body }}
                />
              </li>
            );
          })}
        </ul>

        {/* Civil-rights pledge — final stamp */}
        <div
          data-testid="equity-pledge"
          className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <ShieldCheck className="h-12 w-12 text-[#D4AF37]" />
            </div>
            <div className="lg:col-span-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
                The Bridge Model Civil-Rights Pledge
              </div>
              <p className="font-display text-lg lg:text-xl leading-snug text-[#F9F7F1]">
                Bridge complies with Title VI of the Civil Rights Act, the Fair
                Housing Act, the Americans with Disabilities Act, Section 504,
                and the Age Discrimination Act. Beyond compliance, we hold
                ourselves to a stricter operational standard:{" "}
                <em className="italic text-[#D4AF37]">
                  if a household needs the service and lives in the deployment
                  community, the Service Connector walks alongside them. Period.
                </em>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
